import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { extractError } from "@/lib/api";
import { Lightning, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 hero-grid">
      <div className="w-full max-w-md">
        <div className="border border-border bg-card p-8 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={20} weight="fill" className="text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Sign in</span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-8">Welcome back.</h1>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-black rounded-sm border-border focus-visible:ring-primary"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password"
                  className="text-xs font-mono text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password" type={showPassword ? "text" : "password"} required autoComplete="current-password"
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-black rounded-sm border-border focus-visible:ring-primary pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm"
            >
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            No account?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-primary hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
